import { getLanguage, t } from "@redotech/redo-i18n";
import { Condition } from "@redotech/redo-return-core-common/return-flow/condition";
import { DateOption } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import { CONDITION_NAMES } from "@redotech/redo-return-core-common/return-flow/condition-names";
import {
  orderDateFromModel,
  returnDateFromModel,
  validateDateConditionState,
  type DateConditionState,
} from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { getDateString } from "@redotech/util/date-format";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";

const getDateDescription = (
  state: DateConditionState,
  dateType: "Order" | "Return" | "Request",
) => {
  const locale = getLanguage();
  const date =
    state.start &&
    getDateString(state.start, {
      month: "short",
      day: "numeric",
      year: "numeric",
      locale,
    });
  const start =
    state.start &&
    getDateString(state.start, { month: "short", day: "numeric", locale });
  const end =
    state.end &&
    getDateString(state.end, { month: "short", day: "numeric", locale });
  const isOrder = dateType === "Order";
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (state.dateOption) {
    case DateOption.AFTER:
      return isOrder
        ? t("Order created after {{date}}", { date })
        : t("Request created after {{date}}", { date });
    case DateOption.BEFORE:
      return isOrder
        ? t("Order created before {{date}}", { date })
        : t("Request created before {{date}}", { date });
    case DateOption.BETWEEN:
      return isOrder
        ? t("Order created between {{start}} — {{end}}", { start, end })
        : t("Request created between {{start}} — {{end}}", { start, end });
    default:
      return isOrder ? t("Order created date") : t("Request created date");
  }
};

export const RETURN_DATE = (
  Details: React.ComponentType<ConditionTypeDetailsProps<DateConditionState>>,
): ConditionType<DateConditionState, Condition.ReturnDate> => ({
  name: CONDITION_NAMES[Condition.RETURN_DATE],
  description(state) {
    return getDateDescription(state, "Request");
  },
  Details(props: ConditionTypeDetailsProps<DateConditionState>) {
    return React.createElement(Details, props);
  },
  empty: { start: null, end: null, dateOption: null },
  fromModel(model) {
    return returnDateFromModel(model);
  },
  toModel(state) {
    return {
      type: Condition.RETURN_DATE,
      start:
        state.start &&
        state.start
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      end:
        state.end &&
        state.end
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      dateOption: state.dateOption,
    };
  },
  valid(state) {
    return validateDateConditionState(state).valid;
  },
});

export const ORDER_DATE = (
  Details: React.ComponentType<ConditionTypeDetailsProps<DateConditionState>>,
): ConditionType<DateConditionState, Condition.OrderDate> => ({
  name: CONDITION_NAMES[Condition.ORDER_DATE],
  description(state) {
    return getDateDescription(state, "Order");
  },
  Details(props: ConditionTypeDetailsProps<DateConditionState>) {
    return React.createElement(Details, props);
  },
  empty: { start: null, end: null, dateOption: null },
  fromModel(model) {
    return orderDateFromModel(model);
  },
  toModel(state) {
    return {
      type: Condition.ORDER_DATE,
      start:
        state.start &&
        state.start
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      end:
        state.end &&
        state.end
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      dateOption: state.dateOption,
    };
  },
  valid(state) {
    return validateDateConditionState(state).valid;
  },
});
